import React, { useEffect, useState } from 'react';
import { favoriteAgentTerms, popoverTerms } from '../../../../agent-production/constants/agentProductionConstants';
import { terms } from '../../../../agent-production/constants/savedAgents';
import { useCommonStore, useSearchStore, useSavedAgentsStore } from '../../../../store/store';
import SavedItemDropdown from '../SavedItemDropdown';
import { setStateData } from '../../../helpers/state';
import EditListModal from '../menus/EditListModal';
import { modalKeys } from '../../../helpers/menu';
import { showToast } from '../../../helpers/toast';
import { agentProductionTerms, modules, searchTerms } from '../../../../constants';
import ToastContainerPortal from '../menus/ToastContainerPortal';
import AffectedSavedSearchesModal from '../menus/AffectedSavedSearchesModal';
import { useCloseChip } from './hooks/closeChip';

const SavedAgentListsChip = () => {
    const savedAgentsModule = modules.proficiencyMetrics.savedAgents;
    const {
        selectedList,
        closeEditModal,
        validateAgentListNameAndSave,
        validateSavedSearchAndDelete,
        continueWithSavedSearchesUpdate,
        setSelectedListByModule,
        resetPersistedData,
    } = useSavedAgentsStore(state => ({
        selectedList: state.selectedListByModule[savedAgentsModule],
        closeEditModal: state.closeEditModal,
        validateAgentListNameAndSave: state.validateAgentListNameAndSave,
        validateSavedSearchAndDelete: state.validateSavedSearchAndDelete,
        continueWithSavedSearchesUpdate: state.continueWithSavedSearchesUpdate,
        setSelectedListByModule: state.setSelectedListByModule,
        resetPersistedData: state.resetPersistedData,
    }));
    const {
        agentLists,
        mlsProviders,
    } = useCommonStore(state => ({
        agentLists: state.agentLists,
        mlsProviders: state.mlsProviders,
    }));

    const [state, setState] = useState({
        isPopoverOpen: false,
        isEditMode: false,
        savedAgentGroups: {},
        showEditModal: false,
        isListModalOpen: false,
        editAgentList: null,
        selectedAgentList: undefined,
        showAffectedSavedSearchesModal: false,
        affectedSavedSearches: [],
        savedSearchesUpdatePrompt: false,
    });

    const {
        searchSelectedListId,
    } = useSearchStore(state => ({
        searchSelectedListId: state[state.selectedModule]?.search?.agentListId,
    }));

    useCloseChip(state.isPopoverOpen, setState);

    const setSearchStoreDefaultTitles = () => {
        useSearchStore.setState(state => ({
            [savedAgentsModule]: {
                ...state[savedAgentsModule],
                marketAreaTitle: {
                    mainTitle: `${searchTerms.marketArea}: ${searchTerms.lowerAllMls}`
                },
                propertyTypeTitle: {
                    mainTitle: searchTerms.allResidential
                },
                timeFrameTitle: {
                    mainTitle: agentProductionTerms.last12Months
                }
            }
        }));
    }

    useEffect(() => {
        // Execute default search if there's a selected list
        if (selectedList && searchSelectedListId !== selectedList.agentListId && !state.isLoading) {
            // Update titles directly in search store
            setSearchStoreDefaultTitles();
        }
    }, [selectedList]);

    useEffect(() => {
        let savedAgentGroups = {};
        if (!agentLists?.length > 0 && !mlsProviders?.length > 0) {
            setStateData('savedAgentGroups', savedAgentGroups, setState)
            return;
        };
        const getMlsName = mlsId => mlsProviders?.find(mls => mls.mlsId === mlsId)?.shortDescription;
        savedAgentGroups = agentLists?.reduce((group, ss) => {
            const key = getMlsName(ss.mlsId);
            if (!key) {
                // Assume if MLS name is not found, user no longer has perms to
                // the MLS, so don't display saved searches for that MLS
                return group;
            }
            if (!group[key]) {
                group[key] = [];
            }
            group[key].push({...ss, id: ss.agentListId});
            return group;
        }, {});
        setStateData('savedAgentGroups', savedAgentGroups, setState);
    }, [agentLists, mlsProviders]);

    const togglePopover = () => {
        if (!state.isEditMode) {
            setStateData('isPopoverOpen', !state.isPopoverOpen, setState);
        }
    };

    const setToastProps = (type, message) => showToast(type, message, setState);

    let selectedAgentCriteria, selectedSavedSearch, resetSelectedAgentList; // We need to pass them as undefined in the DeleteAction.

    const handleValidateAgentListNameAndSave = (event, list, newName) => {
        const args = {
            event,
            list,
            newName,
            mlsId: list.mlsId,
            selectedAgentCriteria,
            state,
            setState,
            toggleListModal,
            userId: list.ownerId,
            selectedSavedSearch,
            showToast: setToastProps,
        };
        validateAgentListNameAndSave(args);
    };

    let resetSelectedAgentListTitle; // We need to pass it as undefined in the DeleteAction.

    const toggleListModal = () => {
        setState((prevState) => {
            return {
                ...prevState,
                editAgentList: null,
                showEditModal: !state.showEditModal,
            }
        });
    };

    const handleSelect = (listId) => {
        if (selectedList?.agentListId === listId) return;
        let agentList;
        if (typeof listId === 'string' && listId.includes('all-lists-')) {
            const splitListId = listId.split('-');
            if (splitListId.length < 3 || isNaN(parseInt(splitListId[2], 10))) {
                setToastProps({
                    message: "Invalid list ID format.",
                    type: "error",
                });
                return;
            }
            const mlsId = parseInt(listId.split('-')[2], 10);
            const agentIds = agentLists
                .filter((agent) => agent.mlsId === mlsId)
                .map((agent) => agent.agentIds).flat();
            const uniqueAgentIds = [...new Set(agentIds)];
            agentList = {
                agentListId: listId,
                name: favoriteAgentTerms.reservedListName,
                mlsId: mlsId,
                agentIds: uniqueAgentIds
            };
        } else {
            agentList = agentLists.find(agent => agent.agentListId === listId);
        }
        setSelectedListByModule(savedAgentsModule, agentList);
        resetPersistedData();
        // Update titles directly in search store
        setSearchStoreDefaultTitles();
        useSearchStore.setState({isManualSearch: false});
        togglePopover();
    };

    const handleEdit = (list, e) => {
        e.stopPropagation();
        setState((prevState) => {
            return {
                ...prevState,
                editAgentList: list,
                showEditModal: true
            }
        });
    };

    const getSavedAgentName = (item) => item.name;
    const getSavedAgentMlsId = (item) => item.mlsId;

    const NoSavedAgents = () => (<div className='text-center py-3'>
        {searchTerms.noSavedAgentsLists}
    </div>);

    return (
        <>
            <SavedItemDropdown
                id='saved-agents-chip'
                label={terms.agentLists}
                selectedName={selectedList?.name}
                currentTitle={popoverTerms.currentList}
                listTitle={popoverTerms.savedAgentLists}
                NoItemsComponent={NoSavedAgents}
                getSavedItemName={getSavedAgentName}
                getSavedItemMls={getSavedAgentMlsId}
                initialSelectedItemId={selectedList?.agentListId}
                onSelectItem={handleSelect}
                togglePopover={togglePopover}
                isPopoverOpen={state.isPopoverOpen}
                savedGroups={state.savedAgentGroups}
                handleOpenEditModal={handleEdit}
                filterPlaceholder={searchTerms.nameYourList}
            />
            <AffectedSavedSearchesModal
                showModal={state.showAffectedSavedSearchesModal}
                onClose={() => setStateData('showAffectedSavedSearchesModal', false, setState)}
                onYes={() => continueWithSavedSearchesUpdate(state.selectedAgentList, state.selectedAgentList.ownerId,
                    selectedSavedSearch, resetSelectedAgentList, state, setState, setToastProps, toggleListModal)}
                savedSearches={state.affectedSavedSearches}
                list={state.selectedAgentList}
                continuePrompt={state.savedSearchesUpdatePrompt}
                searchType='Agent'
            />
            {state.editAgentList && <EditListModal
                key={modalKeys.dropdown}
                closeEditModal={() => closeEditModal(setState, toggleListModal)}
                modalIsOpen={state.showEditModal}
                initialList={state.editAgentList}
                idProperty='agentListId'
                onSaveNew={handleValidateAgentListNameAndSave}
                onUpdate={handleValidateAgentListNameAndSave}
                onDelete={(list) => validateSavedSearchAndDelete( list, state, setState, selectedList.ownerId, setToastProps,
                    toggleListModal, resetSelectedAgentListTitle)}
                headerContent={searchTerms.editList}
            />}
            <ToastContainerPortal toastProps={state.toastProps} />
        </>
    );
};

export default SavedAgentListsChip;
